import { FILES_TO_TRACK_DATA } from 'constants/constants';
import { STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';

import { trackMixpanelEvent } from './mixpanel-init';
import { addLogsToSentry } from './sentry';

interface FilesToTrackData {
  regex: RegExp;
  cacheKey: string;
  trackingName: string;
}

interface TrackingResource {
  cacheStorageKey: string;
  trackingName: string;
  deliveryType?: string;
  contentDownloadTime: number;
  waitingForServerFirstResponse: number;
  totalDuration: number;
  url: string;
  bandwidthInMbps: number;
  fileSizekB: number;
}

const getAllResourcesForFileToMonitor = (filesDetailToTrack: FilesToTrackData[]) => {
  const resourcePerformanceData = performance
    .getEntriesByType('resource')
    .filter(item => {
      return item.deliveryType !== 'cache' && item.transferSize > 0;
    }) as PerformanceResourceTiming[];

  return resourcePerformanceData.reduce((performanceDataList, currentValue) => {
    const foundValue = filesDetailToTrack.find(({ regex }) =>
      regex.test(currentValue.name)
    );
    if (foundValue) {
      const fileSizekB = currentValue.transferSize / 1000;

      const contentDownloadTime = Number(
        (currentValue.responseEnd - currentValue.responseStart).toFixed(2)
      );

      /**
       * 1kB*8 = kilobits
       * Dividing size by time - in ms gives MegaBits/s
       * */
      const bandwidthInMbps = Number(((fileSizekB * 8) / contentDownloadTime).toFixed(2));

      performanceDataList.push({
        cacheStorageKey: foundValue?.cacheKey,
        trackingName: foundValue?.trackingName,
        deliveryType: currentValue?.deliveryType,
        fileSizekB: Number(fileSizekB.toFixed(2)),
        contentDownloadTime,
        waitingForServerFirstResponse: Number(
          (currentValue.responseStart - currentValue.requestStart).toFixed(2)
        ),
        url: currentValue.name,
        totalDuration: Number(currentValue.duration.toFixed(2)),
        bandwidthInMbps,
      });
    }
    return performanceDataList;
  }, [] as TrackingResource[]);
};

const accumulateTrackingDataForEachResourceWithHeaders = async (
  data: TrackingResource
) => {
  const { cacheStorageKey, ...performanceResourceData } = data;

  const cachedBucket = await caches.open(cacheStorageKey);

  const cachedResponse = await cachedBucket.match(
    performanceResourceData.url.slice(window.location.origin.length)
  );

  if (cachedResponse) {
    const isCloudFront = cachedResponse.headers.get('X-Amz-Cf-Id');

    let headersData;
    if (isCloudFront) {
      headersData = {
        cdnCacheStatus: cachedResponse.headers.get('X-cache'),
        cdnEdgeLocation: cachedResponse.headers.get('X-Amz-Cf-Pop'),
        webHostingCacheStatus: '',
        webHostingEdgeLocation: cachedResponse.headers.get('X-Served-By'),
      };
    } else {
      headersData = {
        cdnCacheStatus: cachedResponse.headers.get('Cf-Cache-Status'),
        cdnEdgeLocation: cachedResponse.headers.get('Cf-Ray'),
        webHostingCacheStatus: cachedResponse.headers.get('X-Cache'),
        webHostingEdgeLocation: cachedResponse.headers.get('X-Served-By'),
      };
    }

    return { trackedPerformanceData: performanceResourceData, headersData };
  }
  return { trackedPerformanceData: performanceResourceData };
};

const storePerformanceDataToLocalStorage = () => {
  const data = getAllResourcesForFileToMonitor(FILES_TO_TRACK_DATA);

  // if user hard refreshes there will be no data of headers in storage as service worker is bypassed. Hence ignoring that data.
  if (data.length && navigator?.serviceWorker?.controller) {
    localStorage.setItem(
      STORAGE_KEYS.UN_CACHED_NETWORK_PERFORMANCE_DATA,
      JSON.stringify(data)
    );
  }
};

const collectAndPushPerformanceDataToMixpanel = async () => {
  try {
    const networkStoredData = localStorage.getItem(
      STORAGE_KEYS.UN_CACHED_NETWORK_PERFORMANCE_DATA
    );
    if (networkStoredData) {
      const parsedData: TrackingResource[] = JSON.parse(networkStoredData);

      const trackingData = await Promise.all(
        parsedData.map(accumulateTrackingDataForEachResourceWithHeaders)
      );

      trackingData.forEach(item => {
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.NETWORK_DATA_REQUEST, {
          ...item.trackedPerformanceData,
          ...item.headersData,
        });
      });

      localStorage.removeItem(STORAGE_KEYS.UN_CACHED_NETWORK_PERFORMANCE_DATA);
    } else {
      storePerformanceDataToLocalStorage();
    }
  } catch (error) {
    addLogsToSentry({}, error);
  }
};

export {
  getAllResourcesForFileToMonitor,
  accumulateTrackingDataForEachResourceWithHeaders,
  collectAndPushPerformanceDataToMixpanel,
};
